import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '@/lib/http';
import { ApiError } from '@/features/auth/api';
import type { CustomerAddress } from '@/features/customers/api';

export type VatStatus = 'ACTIVE' | 'EXEMPT';

export type NumberingPattern = 'INDEX_YEAR' | 'INDEX_MONTH_YEAR';

export type ReadinessBlocker =
  | 'NO_PROFILE'
  | 'NO_MATCHING_CERT'
  | 'MATCHING_CERT_EXPIRED'
  | 'MATCHING_CERT_REVOKED';

/** A payment bank account on the issuer profile, mirroring the API's `IssuerBankAccountResponse`. */
export interface IssuerBankAccount {
  account: string;
  bankName: string | null;
  isDefault: boolean;
}

/** The standing seller identity, mirroring the API's `IssuerProfileResponse`. */
export interface IssuerProfile {
  nip: string;
  companyName: string;
  address: CustomerAddress;
  /** Server-formatted address lines (the API owns the formatting; the form still edits `address`). */
  addressLines: string[];
  vatStatus: VatStatus;
  exemptionLegalBasis: string | null;
  bankAccounts: IssuerBankAccount[];
  numberingPattern: NumberingPattern;
  contactEmail: string | null;
  contactPhone: string | null;
  krs: string | null;
  regon: string | null;
  includeSellerContact: boolean;
  includeBuyerContact: boolean;
  includeKrs: boolean;
  includeRegon: boolean;
}

/** Server-computed "ready to issue" state, mirroring the API's `IssuerReadinessResponse`. */
export interface IssuerReadiness {
  hasProfile: boolean;
  profileComplete: boolean;
  hasActiveCert: boolean;
  canIssue: boolean;
  blockers: ReadinessBlocker[];
}

/** The `GET`/`PUT /api/me/issuer-profile` payload: the profile (null before setup) plus readiness. */
export interface IssuerProfileEnvelope {
  profile: IssuerProfile | null;
  readiness: IssuerReadiness;
}

/** Full-replace body, mirroring the API's `IssuerProfileRequest` (no ownership or timestamps). */
export interface IssuerProfileInput {
  nip: string;
  companyName: string;
  address: CustomerAddress;
  vatStatus: VatStatus;
  exemptionLegalBasis: string | null;
  bankAccounts: IssuerBankAccount[];
  numberingPattern: NumberingPattern;
  contactEmail: string | null;
  contactPhone: string | null;
  krs: string | null;
  regon: string | null;
  includeSellerContact: boolean;
  includeBuyerContact: boolean;
  includeKrs: boolean;
  includeRegon: boolean;
}

export const ISSUER_PROFILE_QUERY_KEY = ['issuer-profile'] as const;

export function useIssuerProfile() {
  return useQuery<IssuerProfileEnvelope>({
    queryKey: ISSUER_PROFILE_QUERY_KEY,
    queryFn: () => request<IssuerProfileEnvelope>('/api/me/issuer-profile'),
    staleTime: 60_000,
  });
}

export function useSaveIssuerProfile() {
  const qc = useQueryClient();
  return useMutation<IssuerProfileEnvelope, ApiError, IssuerProfileInput>({
    mutationFn: (input) =>
      request<IssuerProfileEnvelope>('/api/me/issuer-profile', {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: (envelope) => qc.setQueryData(ISSUER_PROFILE_QUERY_KEY, envelope),
  });
}
